import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, Select, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { suggestCable, type InstallationMethod, type CableMaterial } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/cable")({
  component: CablePage,
});

function CablePage() {
  const [current, setCurrent] = useState<number | "">("");
  const [method, setMethod] = useState<InstallationMethod>("C");
  const [material, setMaterial] = useState<CableMaterial>("cu");
  const [temp, setTemp] = useState<number | "">(30);
  const [grouping, setGrouping] = useState<number | "">(1);

  const result = useMemo(() => {
    if (current === "" || temp === "" || grouping === "") return null;
    try {
      return suggestCable({
        current: current as number,
        method, material,
        ambientTemp: temp as number,
        groupingFactor: grouping as number,
      });
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [current, method, material, temp, grouping]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Kabeldimensjonering" subtitle="Foreslår tverrsnitt basert på strøm og installasjonsforhold." />
      <CalcShell
        title="Inndata"
        advisory="Veiledende hjelpemiddel. Faktisk kabelvalg må gjøres etter NEK 400 av kvalifisert personell."
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard
              title="Forslag"
              rows={[
                { label: "Tverrsnitt", value: `${result.suggestedCrossSection} mm²`, strong: true },
                { label: "Iz (korrigert)", value: `${fmt(result.iz, 1)} A` },
                { label: "Korreksjonsfaktor", value: fmt(result.correctionFactor, 2) },
                ...result.notes.map((n) => ({ label: "•", value: n })),
              ]}
              note={result.advisory}
            />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Kabeldimensjonering`} lines={linesFromElectricianNote(`Kabel: valgt tverrsnitt`)} />
            </div>
          </>
          ) : <div className="card p-5 text-sm text-muted-foreground">Fyll ut belastningsstrøm.</div>
        }
      >
        <Field label="Belastningsstrøm Ib" unit="A"><NumberInput value={current} onChange={setCurrent} /></Field>
        <Field label="Installasjonsmetode">
          <Select value={method} onChange={setMethod} options={[
            { value: "A1", label: "A1 – i isolert vegg" },
            { value: "A2", label: "A2 – rør i isolert vegg" },
            { value: "B1", label: "B1 – rør på vegg" },
            { value: "B2", label: "B2 – rør i vegg" },
            { value: "C",  label: "C  – på vegg / tak (standard)" },
            { value: "D",  label: "D  – i jord" },
            { value: "E",  label: "E  – fritt i luft" },
          ]} />
        </Field>
        <Field label="Materiale">
          <Select value={material} onChange={setMaterial} options={[
            { value: "cu", label: "Kobber" }, { value: "al", label: "Aluminium" },
          ]} />
        </Field>
        <Field label="Omgivelsestemperatur" unit="°C"><NumberInput value={temp} onChange={setTemp} /></Field>
        <Field label="Grupperingsfaktor"><NumberInput value={grouping} onChange={setGrouping} /></Field>
      </CalcShell>
    </>
  );
}
